import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useLayoutEffect } from 'react';
import { create } from 'zustand';

import type { ProductCartEventInfo } from '@/hooks/products/useCartEventInfo';
import { sendCartEvent } from '@/hooks/products/useCartEventInfo';

type ProductQuantityState = {
  /** Maps productId to quantity. An absent key means 0, matching the controllers' previous `productSelection` semantics. */
  quantities: Map<number, number>;
  setQuantity: (productId: number, quantity: number, cartEventInfo?: ProductCartEventInfo) => void;
  reset: () => void;
};

export const useProductQuantityStore = create<ProductQuantityState>((set, get) => ({
  quantities: new Map(),
  setQuantity: (productId, quantity, cartEventInfo) => {
    if (cartEventInfo) {
      const oldQuantity = get().quantities.get(productId);
      sendCartEvent(oldQuantity, quantity, cartEventInfo);

      if (cartEventInfo.recommendation && !oldQuantity && quantity > 0) {
        datadogRum.addAction('recommendation_add_to_cart', { ...cartEventInfo.recommendation, productId });
      }
    }

    set((state) => {
      const next = new Map(state.quantities);

      if (quantity === 0) {
        next.delete(productId);
      } else {
        next.set(productId, quantity);
      }

      return { quantities: next };
    });
  },
  reset: () => set({ quantities: new Map() }),
}));

// A card reads and writes its own quantity through this hook. The per-id
// selector means one card's stepper does not re-render the whole list.
export const useProductQuantity = (productId: number, cartEventInfo?: ProductCartEventInfo) => {
  const quantity = useProductQuantityStore((state) => state.quantities.get(productId) ?? 0);
  const storeSetQuantity = useProductQuantityStore((state) => state.setQuantity);

  // Keyed on the cart event fields rather than the object, which callers
  // recreate every render, so the setter identity stays stable for the
  // memoization in the cards.
  const { name, price, quantity: cartEventQuantity, recommendation } = cartEventInfo ?? {};
  const impressionId = recommendation?.impressionId;
  const position = recommendation?.position;
  const setQuantity = useCallback(
    (newQuantity: number) =>
      storeSetQuantity(
        productId,
        newQuantity,
        name !== undefined && price !== undefined
          ? {
              name,
              price,
              quantity: cartEventQuantity,
              recommendation:
                impressionId !== undefined && position !== undefined ? { impressionId, position } : undefined,
            }
          : undefined,
      ),
    [productId, storeSetQuantity, name, price, cartEventQuantity, impressionId, position],
  );

  return { quantity, setQuantity };
};

// The controllers' view: everything selected, for checkout / delivery options / persistence.
export const useSelectedProductQuantities = () => useProductQuantityStore((state) => state.quantities);

// Clear all quantities. Called by useResetProductQuantitiesOnMount and by
// test/story setup, e.g. in a `beforeEach`.
export const resetProductQuantities = () => useProductQuantityStore.getState().reset();

// The quantity store is module-global and survives navigation; reset it on
// mount and unmount so a page keeps the lifecycle of the previous per-mount
// useState. useLayoutEffect rather than useEffect so the reset lands before
// paint — a passive effect would let the previous page's quantities render
// for one frame. Nothing seeds the store on page load — order pre-filling
// was removed on both customer types (#7647), so every patient arrives with
// nothing selected.
export const useResetProductQuantitiesOnMount = () => {
  useLayoutEffect(() => {
    resetProductQuantities();
    return resetProductQuantities;
  }, []);
};
